import { crypto as cv, type dto } from '@photo-vault/shared';

// Module-level cache of decrypted tag names per (rootAlbumId, keyGen). A root
// has on the order of tens of tags; decrypting them all on a list-tags call
// is microseconds. Caching saves the work across page navigations within the
// same session — first paint for TagsListPage / TagPicker / chip rendering
// hits the cache, not the WebCrypto subtle pipeline.
//
// Invalidation key includes keyGen so a rotation drops stale entries
// implicitly: post-rotation the next fetch returns rows with bumped keyGen,
// the cache key shifts, the old entries become unreachable. The DTO field is
// optional (older rows default to 1).

interface CacheBucket {
  // tagId → decrypted name (or '' on decrypt failure, surfaced upstream).
  byTagId: Map<string, string>;
}

const buckets = new Map<string, CacheBucket>();

function bucketKey(rootAlbumId: string, keyGen: number): string {
  return `${rootAlbumId}#${keyGen}`;
}

function getBucket(rootAlbumId: string, keyGen: number): CacheBucket {
  const key = bucketKey(rootAlbumId, keyGen);
  let b = buckets.get(key);
  if (!b) {
    b = { byTagId: new Map() };
    buckets.set(key, b);
  }
  return b;
}

export interface DecryptedTag {
  tag: dto.TagDefinition;
  name: string;
}

// Decrypt a list of TagDefinitions in parallel, hitting the cache where
// possible. Failed decryptions surface as the literal '(decrittura fallita)'
// — same affordance as albums.
export async function decryptTagsCached(
  tags: dto.TagDefinition[],
  rootAlbumId: string,
  albumKey: CryptoKey,
): Promise<DecryptedTag[]> {
  return Promise.all(
    tags.map(async (tag) => {
      const keyGen = tag.keyGen ?? 1;
      const bucket = getBucket(rootAlbumId, keyGen);
      const cached = bucket.byTagId.get(tag.id);
      if (cached !== undefined) return { tag, name: cached };
      try {
        const name = await cv.decryptText(tag.encryptedName, albumKey);
        bucket.byTagId.set(tag.id, name);
        return { tag, name };
      } catch {
        return { tag, name: '(decrittura fallita)' };
      }
    }),
  );
}

// Single-tag variant for the deep-link fetch on TagDetailPage.
export async function decryptTagCached(
  tag: dto.TagDefinition,
  rootAlbumId: string,
  albumKey: CryptoKey,
): Promise<DecryptedTag> {
  const [out] = await decryptTagsCached([tag], rootAlbumId, albumKey);
  // Type assertion: the array always has exactly one element here.
  return out as DecryptedTag;
}

// Drop the cached name for a single tag (after a successful rename) so the
// next read decrypts the new ciphertext. We don't have to wait for keyGen
// to bump because rename keeps keyGen stable.
export function forgetTagName(rootAlbumId: string, tagId: string, keyGen: number): void {
  const b = buckets.get(bucketKey(rootAlbumId, keyGen));
  if (b) b.byTagId.delete(tagId);
}

// Drop everything for a root (e.g. after a delete-tag or page reload paths).
// Cheap — only iterates the bucket index, not the entries.
export function forgetRoot(rootAlbumId: string): void {
  for (const k of [...buckets.keys()]) {
    if (k.startsWith(`${rootAlbumId}#`)) buckets.delete(k);
  }
}
